import { Editor, Range, Element as SlateElement, Transforms } from "slate";

export const normalizeSlateValue = (value) => {
    const defaultInitialValue = [
        {
            type: "paragraph",
            children: [{ text: "" }],
        },
    ];
    if (!value || !Array.isArray(value) || value.length === 0) {
        return defaultInitialValue;
    }

    const hasValidNode = value.some(
        (node) =>
            node &&
            node.children &&
            Array.isArray(node.children) &&
            node.children.length > 0
    );

    return hasValidNode ? value : defaultInitialValue;
};

const isSlateNodeEmpty = (node) => {
    if (!node) {
        return true;
    }

    if (typeof node.text === 'string') {
        return node.text.trim().length === 0;
    }

    if (Array.isArray(node.children)) {
        return node.children.every(isSlateNodeEmpty);
    }

    return true;
};

export const isSlateValueEmpty = (value) => {
    if (value === null || value === undefined) {
        return true;
    }

    let parsedValue = value;

    if (typeof value === 'string') {
        if (!value.trim()) {
            return true;
        }

        try {
            parsedValue = JSON.parse(value);
        } catch (error) {
            return value.trim().length === 0;
        }
    }

    if (!Array.isArray(parsedValue)) {
        return true;
    }

    return parsedValue.every(isSlateNodeEmpty);
};

export const withLinks = (editor, disabled = false) => {
    const { isInline } = editor;

    editor.isInline = (element) => {
        if (element.type === 'link') {
            return true;
        }
        return isInline(element);
    };

    editor.disabled = disabled;

    return editor;
};

export const isLinkActive = (editor) => {
    const [link] = Editor.nodes(editor, {
        match: n =>
            !Editor.isEditor(n) &&
            SlateElement.isElement(n) &&
            n.type === 'link',
    });
    return !!link;
};

export const Leaf = ({ attributes, children, leaf }) => {
    if (leaf.bold) {
        children = <strong>{children}</strong>;
    }
    if (leaf.italic) {
        children = <em>{children}</em>;
    }
    if (leaf.underline) {
        children = <u>{children}</u>;
    }
    return <span {...attributes}>{children}</span>;
};

export const isAlignType = (format) => {
    return ["left", "center", "right", "justify"].includes(format);
};
export const isListType = (format) => {
    return ["numbered-list", "bulleted-list"].includes(format);
};
export const isAlignElement = (element) => {
    return "align" in element;
};

export const isMarkActive = (editor, format) => {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
};

export const Element = ({ attributes, children, element, editor }) => {
    const style = {};
    if (isAlignElement(element)) {
        style.textAlign = element.align;
    }
    switch (element.type) {
        case "bulleted-list":
            return (
                <ul style={style} {...attributes}>
                    {children}
                </ul>
            );
        case "heading-one":
            return (
                <h1 style={{ margin: "8px 0", ...style }} {...attributes}>
                    {children}
                </h1>
            );
        case "heading-two":
            return (
                <h2 style={{ margin: "8px 0", ...style }} {...attributes}>
                    {children}
                </h2>
            );
        case "list-item":
            return (
                <li style={style} {...attributes}>
                    {children}
                </li>
            );
        case "numbered-list":
            return (
                <ol style={style} {...attributes}>
                    {children}
                </ol>
            );
        case 'link':
            const isDisabled = editor?.disabled;
            return (
                <a
                    {...attributes}
                    href={element.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                        if (isDisabled) {
                            e.preventDefault();
                        }
                    }}
                    style={{
                        color: isDisabled ? 'rgba(0, 0, 0, 0.38)' : '#1976d2',
                        textDecoration: isDisabled ? 'none' : 'underline',
                        cursor: isDisabled ? 'default' : 'pointer',
                        pointerEvents: isDisabled ? 'none' : 'auto'
                    }}
                >
                    {children}
                </a>
            );

        default:
            return (
                <p style={{ margin: "0px 0", ...style }} {...attributes}>
                    {children}
                </p>
            );
    }
};

export const isBlockActive = (editor, format, blockType = "type") => {
    const { selection } = editor;
    if (!selection) return false;
    const [match] = Array.from(
        Editor.nodes(editor, {
            at: Editor.unhangRange(editor, selection),
            match: (n) => {
                if (!Editor.isEditor(n) && SlateElement.isElement(n)) {
                    if (blockType === "align" && isAlignElement(n)) {
                        return n.align === format;
                    }
                    return n.type === format;
                }
                return false;
            },
        })
    );
    return !!match;
};

export const unwrapLink = (editor) => {
    Transforms.unwrapNodes(editor, {
        match: n =>
            !Editor.isEditor(n) &&
            SlateElement.isElement(n) &&
            n.type === 'link',
        split: true,
        mode: "all",
    });
};

export const wrapLink = (editor, url) => {
    if (isLinkActive(editor)) {
        unwrapLink(editor);
    }

    const { selection } = editor;
    const isCollapsed = selection && Range.isCollapsed(selection);

    if (isCollapsed) {
        // Insert link with URL as text when no text is selected
        const linkNode = {
            type: 'link',
            url,
            children: [{ text: url }],
        };
        Transforms.insertNodes(editor, linkNode);

        // Move cursor after the link and insert a space to break the link
        Transforms.move(editor, { distance: 1, unit: 'offset' });
    } else {
        // Wrap the selected text with link
        Transforms.wrapNodes(
            editor,
            {
                type: 'link',
                url,
                children: [],
            },
            { split: true }
        );

        // Move cursor to the end of the link
        Transforms.collapse(editor, { edge: 'end' });

        // Move the cursor outside the link node
        Transforms.move(editor, { distance: 1, unit: 'offset' });
    }
};
